import { z } from 'zod';
import { HAIR_FEATURES } from './look.js';
/**
 * Cutout rig: how a static reference sprite becomes an animation.
 *
 * Generative frame-by-frame animation was ruled out because it repaints the
 * character every frame, so equipment overlays can never align (ADR 0003).
 * Here every frame is a deterministic recomposition of the SAME pixels: parts
 * are cut from the reference sprite once, then translated by integer offsets.
 *
 * The payoff for the paper doll: an equipment overlay is drawn once on the
 * reference pose, bound to a part, and driven by that part's offsets. It lines
 * up on every animation and every direction by construction, at no extra cost.
 */
export const DIRECTIONS = ['south', 'east', 'north', 'west'];
/**
 * Parts are named by depth relative to the camera, not by the character's own
 * left and right: which limb is nearer flips between facing south and north,
 * and the per-direction draw order is what the compositor actually needs.
 */
export const RIG_PARTS = [
    'head',
    'torso',
    'arm-back',
    'arm-front',
    'leg-back',
    'leg-front',
    /** Quadrupeds only; a humanoid rig simply does not declare it. */
    'tail',
];
/** Character-customisation layers. Their pixels are cut out of the body. */
export const FEATURES = ['eyes', ...HAIR_FEATURES];
/** Attachment points for held equipment. */
export const ANCHORS = ['weapon', 'offhand'];
const HEX_COLOR = /^#[0-9a-f]{6}$/;
export const RigPartSchema = z.enum(RIG_PARTS);
/** Integer pixel offset. Fractional offsets would break pixel-perfect rendering. */
export const OffsetSchema = z.tuple([z.number().int(), z.number().int()]);
/** One offset frame: the parts it moves. A part left out keeps its reference position. */
export const OffsetFrameSchema = z.partialRecord(RigPartSchema, OffsetSchema);
/**
 * One key-pose frame: a whole body image instead of part offsets.
 *
 * Idle and walk are small motions the cutout rig does well; a sword swing
 * twists the torso and stretches the arm in ways translations cannot. Those
 * frames are generated once (Pixellab v3, ADR 0003), quantised to the
 * reference palette and dropped in as images. Features and held equipment
 * are re-derived from landmarks in the image at build time; `anchors` lets a
 * hand-checked position override the detection.
 */
export const PoseFrameSchema = z
    .object({
    /** PNG path relative to the rig's `source`, on the rig canvas. */
    pose: z.string().min(1),
    anchors: z
        .partialRecord(z.enum(ANCHORS), z.object({
        x: z.number().int().min(0),
        y: z.number().int().min(0),
        angle: z.number().int().min(-180).max(180).default(0),
    }))
        .optional(),
})
    .strict();
/**
 * Equipment overlays composed with the body, keyed by the item template's
 * `sprite` key. `held` pieces are stamped at a hand anchor with their own
 * angle (weapons, off-hands); `worn` pieces are drawn once on the reference
 * pose per direction (`<source>-<direction>.png`, greyscale) and ride the part
 * they follow, exactly like a feature (helmets, chest pieces, gauntlets, boots).
 */
export const OverlaySchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('held'),
        anchor: z.enum(ANCHORS),
        /** PNG path relative to the rig's `source`; blade up, grip at the bottom centre. */
        source: z.string().min(1),
        /**
         * Animation frames of the piece itself, same convention as `source`.
         *
         * A flaming blade flickers whatever the body does, so its frames cycle on
         * the body's frame index: frame i of any animation shows piece frame
         * i mod n. Every layer of a character plays the same clip in lockstep, so
         * this costs the client nothing. Omitted, the piece is `source`, still.
         */
        frames: z.array(z.string().min(1)).min(1).optional(),
        /** Pixels above the bottom where the hand closes; see ADR 0003, "Point de prise". */
        gripUp: z.number().int().min(0).default(0),
        /**
         * Degrees that replace the anchor's rest angle for this piece alone, by
         * direction (ADR 0070): a greatsword longer than the others is held more
         * upright in profile, where the usual 45° would push its tip off the
         * 32×32 canvas. Drawn poses keep the hand they find.
         */
        tilt: z.partialRecord(z.enum(DIRECTIONS), z.number().min(-90).max(90)).optional(),
        previewTint: z.string().regex(HEX_COLOR).default('#c9c9d4'),
    }),
    z.object({
        kind: z.literal('worn'),
        /**
         * The part the piece rides, or several: gauntlets ride both arms, boots
         * both legs. With several, each pixel of the drawing rides the listed
         * part under it (the nearest one when it overhangs), and where a part
         * drawn in front covers it, it is hidden, so a glove on the far arm
         * passes behind the torso like the arm does.
         */
        follows: z.union([RigPartSchema, z.array(RigPartSchema).min(1)]),
        /** Path prefix relative to `source`: `<prefix>-<direction>.png` on the rig canvas. */
        source: z.string().min(1),
        /**
         * Animation frames of the piece itself, each a prefix like `source`
         * (ADR 0031). Like a held piece's, they cycle on the body's frame index:
         * frame i of any animation wears piece frame i mod n, so a helm's flames
         * flicker whatever the body does. Omitted, the piece is `source`, still.
         */
        frames: z.array(z.string().min(1)).min(1).optional(),
        /**
         * Worn on the back, behind the body (wings, ADR 0053): it rides the torso
         * without being a plate, so a swing never paints the torso it uncovers in
         * its colours, and a piece nearly as wide as the canvas may lose a tip at
         * the border for a frame rather than refuse the build.
         */
        back: z.boolean().default(false),
        previewTint: z.string().regex(HEX_COLOR).default('#a8a8b4'),
    }),
]);
const OVERLAY_KEY = /^overlay\.[a-z0-9-]+$/;
/** A pose comes first: an offset frame never carries a `pose` key, so the union is unambiguous. */
export const RigFrameSchema = z.union([PoseFrameSchema, OffsetFrameSchema]);
export function isPoseFrame(frame) {
    return typeof frame.pose === 'string';
}
/**
 * Frames are per direction: walking south swings the legs up and down the
 * screen, walking east swings them left and right. Every direction must hold
 * the same number of frames so the client can treat one animation as one clip.
 */
export const RigAnimationSchema = z
    .object({
    frameDurationMs: z.number().int().min(16).max(2000),
    loop: z.boolean().default(true),
    /**
     * Parts left out of every frame. A rider's legs are inside the mount, so
     * `ride` hides them rather than drawing them over the horse's flank.
     */
    hide: z.array(RigPartSchema).default([]),
    frames: z.record(z.enum(DIRECTIONS), z.array(RigFrameSchema).min(1).max(16)),
})
    .superRefine((animation, ctx) => {
    const counts = new Set(DIRECTIONS.map((direction) => animation.frames[direction].length));
    if (counts.size > 1) {
        ctx.addIssue({
            code: 'custom',
            path: ['frames'],
            message: `All directions must have the same frame count, got ${[...counts].sort().join('/')}`,
        });
    }
});
export const RigDefinitionSchema = z
    .object({
    version: z.literal(1),
    /** Logical key of the body this rig drives, e.g. `body.warrior`. */
    key: z.string().min(1),
    /** Directory of `idle-<direction>.png` and `parts-<direction>.png`, relative to this file. */
    source: z.string().min(1),
    canvas: z.object({
        width: z.number().int().min(8).max(128),
        height: z.number().int().min(8).max(128),
    }),
    /** Exact reference colours extracted into a tintable skin layer after composition. */
    skinColors: z.array(z.string().regex(HEX_COLOR)).min(1).optional(),
    /** Part map colour → part name. The part map is a PNG beside the body sprite. */
    parts: z.partialRecord(RigPartSchema, z.object({
        color: z.string().regex(HEX_COLOR, 'expected #rrggbb lowercase'),
        /**
         * Pixels of neighbouring parts to pull into this layer, in pixels.
         *
         * Cutout animation's classic seam problem: move a part by one pixel and
         * a one-pixel hole opens at the joint. Overlapping the layers at the
         * joint fills it — in the neutral pose the part drawn later hides the
         * bleed, and only a moved part reveals it, which is exactly when it is
         * needed. Keep it to 1-2 px: a large bleed drags neighbouring pixels.
         */
        bleed: z.number().int().min(0).max(4).default(0),
    })),
    /**
     * Tintable layers cut out of the body: eyes, and later hair.
     *
     * They are not rig parts — they never move on their own. They ride the part
     * they follow, and live in their own layer purely so a colour can be chosen
     * per character. The build writes them as white silhouettes; the client
     * tints them, which is why the body frames have a hole where they sit.
     */
    features: z
        .partialRecord(z.enum(FEATURES), z.object({
        follows: RigPartSchema,
        /** Colour used to preview the feature, and the default for a new character. */
        tint: z.string().regex(HEX_COLOR, 'expected #rrggbb lowercase'),
        /**
         * Keep the mask's own greyscale instead of flattening it to white.
         *
         * A tint multiplies, so a white silhouette gives a flat colour —
         * right for a two-pixel iris, wrong for hair, which needs shading to
         * read as volume. A shaded mask is authored in greys and tinted.
         */
        shaded: z.boolean().default(false),
        /**
         * The feature sits ON the body rather than replacing part of it.
         *
         * Eyes are painted into the reference body, so their pixels must
         * leave it or the baked blue shows through a brown tint. Hair is
         * drawn beside a bald reference: the skull is complete underneath,
         * and cutting it out left a hole wherever a helmet hid the hair.
         */
        overlay: z.boolean().default(false),
    }))
        .default({}),
    /**
     * Where held equipment attaches on the reference pose, per direction.
     *
     * A weapon is drawn once, grip at its bottom centre, blade up. At rest it
     * keeps its own angle (BrowserQuest holds the sword upright beside the body)
     * rather than following the arm; the anchor rides the part it is bound to,
     * so idle and walk carry it without any extra data. Keyframe animations
     * (attack) override the angle per frame.
     */
    anchors: z
        .partialRecord(z.enum(ANCHORS), z.record(z.enum(DIRECTIONS), z.object({
        part: RigPartSchema,
        x: z.number().int().min(0),
        y: z.number().int().min(0),
        /** Degrees clockwise from blade-up. Multiples of 15 keep pixel art legible. */
        angle: z.number().int().min(-180).max(180).default(0),
        /** Draw behind the body (facing away from the camera). */
        behind: z.boolean().default(false),
        /**
         * Redraw the carrying hand over the grip. Off by default: with a
         * two-pixel fist the item reads better drawn whole, in front.
         */
        fistOver: z.boolean().default(false),
    })))
        .default({}),
    /** Back-to-front order per direction. Limbs pass in front when facing the camera. */
    drawOrder: z.record(z.enum(DIRECTIONS), z.array(RigPartSchema)),
    animations: z.record(z.string().min(1), RigAnimationSchema),
    overlays: z.record(z.string().regex(OVERLAY_KEY, 'overlay.<name>'), OverlaySchema).default({}),
})
    .superRefine((rig, ctx) => {
    const declared = Object.keys(rig.parts);
    const seenColors = new Map();
    for (const part of declared) {
        const color = rig.parts[part]?.color;
        if (color === undefined)
            continue;
        const previous = seenColors.get(color);
        if (previous !== undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['parts', part, 'color'],
                message: `Colour ${color} already maps to "${previous}"`,
            });
        }
        seenColors.set(color, part);
    }
    for (const direction of DIRECTIONS) {
        // zod already rejected a missing direction: drawOrder is a full record.
        const order = rig.drawOrder[direction];
        const missing = declared.filter((part) => !order.includes(part));
        const unknown = order.filter((part) => !declared.includes(part));
        const duplicated = order.filter((part, index) => order.indexOf(part) !== index);
        for (const [problem, parts] of [
            ['missing from', missing],
            ['not declared in parts:', unknown],
            ['listed twice in', duplicated],
        ]) {
            if (parts.length > 0) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['drawOrder', direction],
                    message: `${parts.join(', ')} ${problem} the draw order`,
                });
            }
        }
    }
    for (const [name, byDirection] of Object.entries(rig.anchors)) {
        for (const direction of DIRECTIONS) {
            const anchor = byDirection[direction];
            if (!declared.includes(anchor.part)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['anchors', name, direction, 'part'],
                    message: `Anchor "${name}" (${direction}) is bound to "${anchor.part}", which is not a declared part`,
                });
            }
            if (anchor.x >= rig.canvas.width || anchor.y >= rig.canvas.height) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['anchors', name, direction],
                    message: `Anchor "${name}" (${direction}) lies outside the canvas`,
                });
            }
        }
    }
    for (const [name, overlay] of Object.entries(rig.overlays)) {
        if (overlay.kind === 'worn') {
            const follows = Array.isArray(overlay.follows) ? overlay.follows : [overlay.follows];
            for (const part of follows) {
                if (declared.includes(part))
                    continue;
                ctx.addIssue({
                    code: 'custom',
                    path: ['overlays', name, 'follows'],
                    message: `Overlay "${name}" follows "${part}", which is not a declared part`,
                });
            }
        }
        if (overlay.kind === 'held' && rig.anchors[overlay.anchor] === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['overlays', name, 'anchor'],
                message: `Overlay "${name}" needs the "${overlay.anchor}" anchor to be declared`,
            });
        }
    }
    for (const [name, feature] of Object.entries(rig.features)) {
        if (!declared.includes(feature.follows)) {
            ctx.addIssue({
                code: 'custom',
                path: ['features', name, 'follows'],
                message: `Feature "${name}" follows "${feature.follows}", which is not a declared part`,
            });
        }
    }
    for (const [name, animation] of Object.entries(rig.animations)) {
        for (const direction of DIRECTIONS) {
            animation.frames[direction].forEach((frame, index) => {
                if (isPoseFrame(frame))
                    return;
                for (const part of Object.keys(frame)) {
                    if (!declared.includes(part)) {
                        ctx.addIssue({
                            code: 'custom',
                            path: ['animations', name, 'frames', direction, index, part],
                            message: `Unknown part "${part}"`,
                        });
                    }
                }
            });
        }
    }
});
export class RigError extends Error {
}
function animationOf(rig, name) {
    const animation = rig.animations[name];
    if (animation === undefined) {
        throw new RigError(`Unknown animation "${name}" in rig "${rig.key}"`);
    }
    return animation;
}
/**
 * Resolve one frame into back-to-front placements.
 *
 * The compositor draws layers in the returned order; a layer bound to a part
 * (a body cutout, or an equipment overlay) uses that part's offset, which is
 * why overlays follow the body without any per-overlay animation data.
 */
export function resolveFramePlacements(rig, animationName, direction, frameIndex) {
    const animation = animationOf(rig, animationName);
    const frames = animation.frames[direction];
    const frame = frames[frameIndex];
    if (frame === undefined) {
        throw new RigError(`Frame ${String(frameIndex)} out of range for "${animationName}" facing ${direction} (${String(frames.length)} frames)`);
    }
    // A key pose replaces the parts wholesale: nothing to place.
    if (isPoseFrame(frame))
        return [];
    return rig.drawOrder[direction].map((part) => {
        const offset = frame[part] ?? [0, 0];
        return { part, dx: offset[0], dy: offset[1] };
    });
}
/** The frame definition itself, for callers that need to know a pose from offsets. */
export function frameAt(rig, animationName, direction, frameIndex) {
    const frames = animationOf(rig, animationName).frames[direction];
    const frame = frames[frameIndex];
    if (frame === undefined) {
        throw new RigError(`Frame ${String(frameIndex)} out of range for "${animationName}" facing ${direction} (${String(frames.length)} frames)`);
    }
    return frame;
}
/** Frames in an animation. Equal across directions, enforced by the schema. */
export function frameCount(rig, animationName) {
    return animationOf(rig, animationName).frames.south.length;
}
/**
 * Extent of the offsets a frame applies, in pixels.
 *
 * This is deliberately NOT the composed frame's bounds: a part covers only part
 * of the canvas, so whether a frame clips depends on each layer's actual pixels.
 * `build:rig` owns that check, where the pixels are known.
 */
export function offsetExtent(rig, animationName, direction, frameIndex) {
    const placements = resolveFramePlacements(rig, animationName, direction, frameIndex);
    if (placements.length === 0)
        return { minDx: 0, minDy: 0, maxDx: 0, maxDy: 0 };
    const xs = placements.map((placement) => placement.dx);
    const ys = placements.map((placement) => placement.dy);
    return {
        minDx: Math.min(...xs),
        minDy: Math.min(...ys),
        maxDx: Math.max(...xs),
        maxDy: Math.max(...ys),
    };
}
/** Largest absolute offset used anywhere in the rig; a sanity bound for the canvas. */
export function maxAbsoluteOffset(rig) {
    let largest = 0;
    for (const animation of Object.values(rig.animations)) {
        for (const direction of DIRECTIONS) {
            for (const frame of animation.frames[direction]) {
                if (isPoseFrame(frame))
                    continue;
                for (const offset of Object.values(frame)) {
                    largest = Math.max(largest, Math.abs(offset[0]), Math.abs(offset[1]));
                }
            }
        }
    }
    return largest;
}
//# sourceMappingURL=rig.js.map